import React from "react";
import {Alert, Box, CircularProgress, Container, Divider, Paper, Snackbar, Stack, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import {useBookingDetails} from "../../hooks/useBookingDetails";

// Components
import BookingHeader from "../../components/booking/BookingHeader";
import BookingInfo from "../../components/booking/BookingInfo";
import BookingContent from "../../components/booking/BookingContent";
import BookingActions from "../../components/booking/BookingActions";
import BookingLogs from "../../components/booking/BookingLogs";

// Dialogs
import ClientDialog from "../../components/booking/dialogs/ClientDialog";
import ContractsDialog from "../../components/booking/dialogs/ContractsDialog";
import EditNotesDialog from "../../components/booking/dialogs/EditNotesDialog";
import ContractScannerDialog from "../../components/booking/dialogs/ContractScannerDialog";

window.activeCameraTracks = [];
if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    const originalGetUserMedia = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);

    navigator.mediaDevices.getUserMedia = async (constraints) => {
        const stream = await originalGetUserMedia(constraints);
        stream.getTracks().forEach(track => {
            window.activeCameraTracks.push(track);
        });
        return stream;
    };
}

export default function BookingDetails() {
    const navigate = useNavigate();
    const {
        reservation,
        categories,
        loading,
        loadingChangeStatus,
        isMobile,
        snack,
        setSnack,
        // Dialogs states
        clientDialogOpen,
        setClientDialogOpen,
        contractsDialogOpen,
        setContractsDialogOpen,
        editNotesDialogOpen,
        setEditNotesDialogOpen,
        confirmDialogToCancelBookingOpen,
        setConfirmDialogToCancelBookingOpen,
        confirmDialogToValidateBookingOpen,
        setConfirmDialogToValidateBookingOpen,
        confirmDialogToSetReadyBookingOpen,
        setConfirmDialogToSetReadyBookingOpen,
        confirmDialogToCompleteBookingOpen,
        setConfirmDialogToCompleteBookingOpen,
        confirmDialogToSendReviewEmailOpen,
        setConfirmDialogToSendReviewEmailOpen,
        confirmDialogToSendLastContract,
        setConfirmDialogToSendLastContract,
        dialogScanOpen,
        setDialogScanOpen,
        // Scanner specific
        showScanner,
        setShowScanner,
        scannedImages,
        setScannedImages,
        signedContractPdf,
        setSignedContractPdf,
        signedContractInputRef,
        // Notes specific
        notes,
        setNotes,
        editNotesLoading,
        // Handlers
        handleGenerateQuote,
        handleGenerateContract,
        handleChangeStatus,
        handleSaveNotes,
        handleSendMail,
        handleCancelBooking,
        handleCapture,
        handleUploadScannedContract,
        handleSignedContractPdfChange,
        calculateTotal
    } = useBookingDetails();

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{
                mt: 4,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "60vh"
            }}>
                <Stack spacing={2} alignItems="center">
                    <Box>
                        <Typography variant="h6" sx={{mb: 2}}>Chargement de la réservation...</Typography>
                        <Box sx={{display: "flex", justifyContent: "center"}}>
                            <CircularProgress color="primary"/>
                        </Box>
                    </Box>
                </Stack>
            </Container>
        );
    }

    return (
        <Container sx={{pb: 6}}>
            <BookingHeader reservation={reservation}/>

            <Divider sx={{my: 2}}/>

            <Box
                sx={{
                    display: "grid", gap: 2,
                    gridTemplateColumns: {xs: "1fr", md: `calc(70% - 8px) calc(30% - 8px)`},
                }}
            >
                <Paper sx={{maxWidth: "100%"}}>
                    <BookingInfo
                        reservation={reservation}
                        onEditNotes={() => {
                            setEditNotesDialogOpen(true);
                            setNotes(reservation.notes || "");
                        }}
                    />

                    <Divider sx={{my: 1}}/>

                    <BookingContent
                        reservation={reservation}
                        categories={categories}
                        isMobile={isMobile}
                        calculateTotal={calculateTotal}
                    />

                    <BookingLogs logs={reservation.logs}/>
                </Paper>

                <Box>
                    <BookingActions
                        reservation={reservation}
                        loadingChangeStatus={loadingChangeStatus}
                        onStatusChange={handleChangeStatus}
                        onEdit={() => navigate(`/bookings/edit/${reservation.id}`)}
                        onCancel={() => setConfirmDialogToCancelBookingOpen(true)}
                        onViewClient={() => setClientDialogOpen(true)}
                        onViewContracts={() => setContractsDialogOpen(true)}
                        onScanContract={() => setDialogScanOpen(true)}
                        onGenerateQuote={handleGenerateQuote}
                        onSendMail={handleSendMail}
                        onGenerateContract={handleGenerateContract}
                        setConfirmSetReadyOpen={setConfirmDialogToSetReadyBookingOpen}
                        setConfirmCompleteOpen={setConfirmDialogToCompleteBookingOpen}
                        setConfirmDialogToSendReviewEmailOpen={setConfirmDialogToSendReviewEmailOpen}
                    />
                </Box>
            </Box>

            {/* Dialogs */}
            <ClientDialog
                open={clientDialogOpen}
                onClose={() => setClientDialogOpen(false)}
                client={reservation.client}
                onViewFullProfile={() => {
                    setClientDialogOpen(false);
                    navigate(`/clients/details/${reservation.client.id}`);
                }}
            />

            <ContractsDialog
                open={contractsDialogOpen}
                onClose={() => setContractsDialogOpen(false)}
                contracts={reservation.contracts}
                onGenerateContract={(v, s) => {
                    handleGenerateContract(v, s);
                    setContractsDialogOpen(false);
                }}
                onSendLastContract={() => setConfirmDialogToSendLastContract(true)}
            />

            <EditNotesDialog
                open={editNotesDialogOpen}
                onClose={() => setEditNotesDialogOpen(false)}
                notes={notes}
                onNotesChange={setNotes}
                onSave={handleSaveNotes}
                loading={editNotesLoading}
            />

            <ContractScannerDialog
                open={dialogScanOpen}
                onClose={() => {
                    setDialogScanOpen(false);
                    setShowScanner(false);
                    setScannedImages([]);
                    setSignedContractPdf(null);
                }}
                showScanner={showScanner}
                setShowScanner={setShowScanner}
                scannedImages={scannedImages}
                clearContent={() => {
                    setScannedImages([]);
                    setSignedContractPdf(null);
                }}
                onCapture={handleCapture}
                signedContractPdf={signedContractPdf}
                onPdfChange={handleSignedContractPdfChange}
                signedContractInputRef={signedContractInputRef}
                onUpload={handleUploadScannedContract}
            />

            {/* Confirm dialogs */}
            {confirmDialogToSendLastContract && (
                <ConfirmDialog
                    title="Envoyer le dernier contrat par mail"
                    content="Êtes-vous sûr de vouloir envoyer le dernier contrat existant par mail ?"
                    onClose={(confirmed) => {
                        setConfirmDialogToSendLastContract(false);
                        if (confirmed) {
                            handleSendMail('contract');
                            setContractsDialogOpen(false);
                        }
                    }}
                />
            )}

            {confirmDialogToCancelBookingOpen && (
                <ConfirmDialog
                    title="Annuler la réservation"
                    content="Êtes-vous sûr de vouloir annuler cette réservation ? Cette action est irréversible."
                    onClose={(confirmed) => {
                        setConfirmDialogToCancelBookingOpen(false);
                        if (confirmed) {
                            handleCancelBooking();
                        }
                    }}
                />
            )}

            {confirmDialogToValidateBookingOpen && (
                <ConfirmDialog
                    title="Valider la réservation"
                    content={<>Êtes-vous sûr de vouloir valider cette réservation ?<br/></>}
                    onClose={(confirmed) => {
                        setConfirmDialogToValidateBookingOpen(false);
                        if (confirmed) {
                            handleChangeStatus("confirmed");
                        }
                    }}
                />
            )}

            {confirmDialogToSetReadyBookingOpen && (
                <ConfirmDialog
                    title="Marquer la réservation comme prête"
                    content={<>Êtes-vous sûr de vouloir marquer cette réservation comme prête ?<br/><br/>Le client
                        sera notifié que tout est prêt pour l'événement et que la facture de solde est réglée.</>}
                    onClose={(confirmed) => {
                        setConfirmDialogToSetReadyBookingOpen(false);
                        if (confirmed) {
                            handleChangeStatus("ready");
                        }
                    }}
                />
            )}

            {confirmDialogToCompleteBookingOpen && (
                <ConfirmDialog
                    title="Terminer la réservation"
                    content={<>Êtes-vous sûr de vouloir marquer cette réservation comme terminée ?</>}
                    onClose={(confirmed) => {
                        setConfirmDialogToCompleteBookingOpen(false);
                        if (confirmed) {
                            handleChangeStatus("completed");
                            setConfirmDialogToSendReviewEmailOpen(true);
                        }
                    }}
                />
            )}

            {confirmDialogToSendReviewEmailOpen && (
                <ConfirmDialog
                    title="Demande d'avis Google"
                    content={<>Souhaitez-vous envoyer le mail de demande d'avis Google au client ?</>}
                    onClose={(confirmed) => {
                        setConfirmDialogToSendReviewEmailOpen(false);
                        if (confirmed) {
                            handleSendMail('review');
                        }
                    }}
                />
            )}

            {/* Snackbar */}
            <Snackbar
                open={snack.open}
                autoHideDuration={2500}
                onClose={() => setSnack({...snack, open: false})}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
            >
                <Alert onClose={() => setSnack({...snack, open: false})} severity={snack.severity} sx={{width: "100%"}}>
                    {snack.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}
